import React from 'react';
import { useQuery } from 'apollo-3';
import styled from 'styled-components';

import { Box, Flex, Image, SvgIcon, Text } from 'Components/Base';
import { getMyUserIconInfo } from 'GraphQL/query/MyProfile.query';
import { getClassroomProviderData } from 'Util/classroomProviderUtils';
import { useConfig } from 'Context/ConfigContext';
import useSidebarMenu from 'Util/Hooks/useSidebarMenu';

const LOGO_SECTION_HEIGHT = 164;

const ImageProfile = styled(Image)`
  border-radius: 100%;
  width: 40px;
  height: 40px;
`;

const MenuItem = styled((props) => <Flex p={4} alignItems={'center'} {...props} />)`
  min-height: 60px;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryLighter};
  }
`;

const Divider = styled(Box)`
  border-top: ${({ theme: { colors } }) => `1px solid ${colors.bsGrays.gray200}`};
  border-bottom: ${({ theme: { colors } }) => `3px solid ${colors.notiGrey.grey3}`};
`;

const ScrollableBox = styled(Box)`
  max-height: ${`calc(100vh - ${LOGO_SECTION_HEIGHT}px)`};
  overflow-y: scroll;
`;

const EXTERNAL_PROFILE_QUERY_OPTIONS = { variables: { includeExternalProfile: true } };

const Sidebar = (props) => {
  const { data, loading } = useQuery(getMyUserIconInfo, EXTERNAL_PROFILE_QUERY_OPTIONS);
  const { appVersion, myCourses } = useConfig();
  const { profilePic: imageProfileUrl, displayName } = data?.myProfile ?? {};
  const providerName = data?.externalProvider?.providerName;

  const classroomProviderData = getClassroomProviderData(providerName);
  const { externalLinks = [] } = classroomProviderData ?? {};
  const menuList = useSidebarMenu(externalLinks);

  if (loading) return null;

  const sidebarConfig = myCourses?.sidebar ?? {};
  const { phoneNo, workTime, ownerLogoUrl } = sidebarConfig;

  return (
    <Box py={5} width={'calc(6 / 7 * 100vw)'} maxWidth={'330px'} {...props}>
      <Box height={LOGO_SECTION_HEIGHT - 40}>
        <Box px={'52px'}>
          <Image
            height="56px"
            width="100%"
            maxWidth="256px"
            style={{ objectFit: 'contain', display: 'block' }}
            src={ownerLogoUrl}
          />
        </Box>
        <Flex px={4} mt={7} alignItems={'center'}>
          <ImageProfile
            src={
              imageProfileUrl && imageProfileUrl !== null
                ? imageProfileUrl
                : '/assets/images/classroom/userAvatar.svg'
            }
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = '/assets/images/classroom/userAvatar.svg';
            }}
          />
          <Text ml={4}>{displayName}</Text>
        </Flex>
      </Box>
      <ScrollableBox mt={4}>
        {menuList.map((menu, index) => {
          if (menu.key === 'divider') {
            return <Divider key={`${menu.key} -${index}`} />;
          }
          return (
            <MenuItem onClick={menu.onClick} key={`${menu.text} - ${index} `}>
              <SvgIcon icon={menu.icon} color={'primary'} size={24} />
              <Text ml={3}>{menu.text}</Text>
            </MenuItem>
          );
        })}
      </ScrollableBox>
      <Divider />
      {phoneNo && (
        <Flex mt={5} px={4} alignItems={'flex-start'}>
          <SvgIcon icon={SvgIcon.ICON_LIST.PHONE} color={'primary'} size={24} />
          <Text textStyle={'caption'} fontWeight={'normal'} ml={3}>
            ติดต่อ Call center: {phoneNo}
            <br />
            (เวลาทำการ {workTime})
          </Text>
        </Flex>
      )}
      {appVersion && (
        <Flex mt={5} px={4} alignItems={'flex-start'}>
          <Box pl={6} />
          <Text textStyle={'caption'} fontWeight={'normal'} ml={3}>
            Version {appVersion}
          </Text>
        </Flex>
      )}
    </Box>
  );
};

export default Sidebar;
